import React from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2, User, FileText } from "lucide-react";
import { fetchAPI } from "../../config/api";
import { PLAYER_HEADSHOT_FALLBACK } from "../../config/constants";

const formatHeight = (h) => {
  if (!h) return null;
  if (typeof h === "string" && h.includes("-")) return h;
  if (typeof h === "number") return `${Math.floor(h / 12)}-${h % 12}`;
  return h;
};

const hasScoutingInfo = (player, isPitcher) => {
  if (player.writeup && player.writeup.trim()) return true;
  if (isPitcher) {
    if (player.pitchArsenal && player.pitchArsenal.length > 0) return true;
    if (player.firstPitchRHH || player.firstPitchLHH) return true;
    if (player.twoStrikePitchRHH || player.twoStrikePitchLHH) return true;
    if (player.timeToPlate) return true;
  } else {
    if (player.hitterType) return true;
    if (player.hotZones && player.hotZones.length > 0) return true;
    if (player.coldZones && player.coldZones.length > 0) return true;
    if (player.isStolenBaseThreat) return true;
    if (player.isGoodWithTwoStrikes) return true;
  }
  return false;
};

const PlayerCard = ({ player, stats, rank, isPitcher, onEdit, onView, onDelete, loading }) => {
  const [imageError, setImageError] = React.useState(false);
  const navigate = useNavigate();

  const headshotUrl = stats?.headshot_url || PLAYER_HEADSHOT_FALLBACK;
  const battingStats = stats?.batting_stats?.[0] || {};
  const pitchingStats = stats?.pitching_stats?.[0] || {};
  const height = formatHeight(stats?.height);
  const hasScouting = hasScoutingInfo(player, isPitcher);
  const playerId = player.id || player.playerId;

  const handleNameClick = (e) => {
    e.preventDefault();
    if (playerId) {
      navigate(`/player/${playerId}`);
    }
  };

  return (
    <div className="group relative bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
      <div className="flex gap-4 p-4">
        <div className="relative flex-shrink-0">
          <div className={`relative w-14 h-14 rounded-xl overflow-hidden ring-2 ${isPitcher ? 'ring-emerald-200' : 'ring-blue-200'}`}>
            {!imageError ? (
              <img
                src={headshotUrl}
                alt={player.name}
                onError={() => setImageError(true)}
                className="w-full h-full object-cover object-top"
              />
            ) : (
              <div className={`w-full h-full flex items-center justify-center ${isPitcher ? 'bg-emerald-50' : 'bg-blue-50'}`}>
                <User className={`w-6 h-6 ${isPitcher ? 'text-emerald-300' : 'text-blue-300'}`} />
              </div>
            )}
            <div className={`absolute bottom-0 right-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${isPitcher ? 'bg-emerald-500' : 'bg-blue-500'}`}>
              {rank}
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1.5">
            {playerId ? (
              <button
                onClick={handleNameClick}
                className="text-sm font-semibold text-gray-900 truncate hover:text-blue-600 hover:underline transition-colors text-left"
              >
                {player.name}
              </button>
            ) : (
              <h3 className="text-sm font-semibold text-gray-900 truncate">{player.name}</h3>
            )}
            {hasScouting && (
              <span 
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                  isPitcher ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                }`}
                title="Has scouting notes"
              >
                <FileText size={10} />
                Report
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-1 mt-0.5 text-[11px] text-gray-400 flex-wrap">
            <span className={`px-1.5 py-0.5 font-medium rounded ${
              isPitcher ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'
            }`}>
              {isPitcher ? player.role || 'P' : player.position || '—'}
            </span>
            {height && <span className="ml-1">{height}</span>}
            {stats?.bats && stats?.throws && (
              <span>• B/T: {stats.bats?.[0].toUpperCase()}/{stats.throws?.[0].toUpperCase()}</span>
            )}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
            {loading ? (
              <>
                <div className="w-16 h-3 bg-gray-100 rounded animate-pulse" />
                <div className="w-12 h-3 bg-gray-100 rounded animate-pulse" />
              </>
            ) : isPitcher ? (
              <>
                <span><span className="font-semibold text-gray-900">{pitchingStats.era != null ? Number(pitchingStats.era).toFixed(2) : "—"}</span> <span className="text-gray-400">ERA</span></span>
                <span><span className="font-semibold text-gray-900">{pitchingStats.fip != null ? Number(pitchingStats.fip).toFixed(2) : "—"}</span> <span className="text-gray-400">FIP</span></span>
                <span><span className="font-semibold text-gray-900">{(pitchingStats.k_pct ?? 0).toFixed(0)}%</span> <span className="text-gray-400">K</span></span>
                <span><span className="font-semibold text-gray-900">{(pitchingStats.war ?? 0).toFixed(1)}</span> <span className="text-gray-400">WAR</span></span>
              </>
            ) : (
              <>
                <span className="font-semibold text-gray-900">{battingStats.ba?.toFixed(3) || "—"}/{battingStats.ob_pct?.toFixed(3) || "—"}/{battingStats.slg_pct?.toFixed(3) || "—"}</span>
                <span><span className="font-semibold text-gray-900">{battingStats.hr ?? "—"}</span> <span className="text-gray-400">HR</span></span>
                <span><span className="font-semibold text-gray-900">{(battingStats.war ?? 0).toFixed(1)}</span> <span className="text-gray-400">WAR</span></span>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            onClick={() => onEdit({ ...player, bats: stats?.bats[0]?.toUpperCase(), throws: stats?.throws[0]?.toUpperCase(), position: player.position || stats?.position })}
            className={`p-1.5 rounded-lg transition-colors ${
              isPitcher 
                ? 'text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50' 
                : 'text-blue-500 hover:text-blue-600 hover:bg-blue-50'
            }`}
            title="Edit notes"
          >
            <Edit size={14} />
          </button>
          <button
            onClick={() => onDelete(player.id)}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove from report"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export const PlayerGallery = ({
  players,
  onEditPlayer,
  onViewPlayer,
  onDeletePlayer,
  isPitcherGallery,
}) => {
  const [statsMap, setStatsMap] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  // Fetch all player stats in bulk
  React.useEffect(() => {
    if (!players || players.length === 0) return;

    const playerIds = players.map(p => p.id).filter(Boolean);
    if (playerIds.length === 0) return;

    const fetchBulkStats = async () => {
      setLoading(true);
      try {
        const response = await fetchAPI('/api/players/bulk', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ player_ids: playerIds }),
        });
        if (response) {
          setStatsMap(response);
        }
      } catch (err) {
        console.error("Error fetching bulk player stats:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBulkStats();
  }, [players]);

  const sortedPlayers = React.useMemo(() => {
    if (!players) return [];
    return [...players].sort((a, b) => {
      const statsA = statsMap[a.id];
      const statsB = statsMap[b.id];
      const warA = isPitcherGallery 
        ? statsA?.pitching_stats?.[0]?.war ?? 0
        : statsA?.batting_stats?.[0]?.war ?? 0;
      const warB = isPitcherGallery 
        ? statsB?.pitching_stats?.[0]?.war ?? 0
        : statsB?.batting_stats?.[0]?.war ?? 0;
      return warB - warA;
    });
  }, [players, statsMap, isPitcherGallery]);

  if (!players || players.length === 0) {
    return (
      <div className="text-center py-16">
        <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
          isPitcherGallery ? 'bg-emerald-50' : 'bg-blue-50'
        }`}>
          <User className={`w-6 h-6 ${isPitcherGallery ? 'text-emerald-300' : 'text-blue-300'}`} />
        </div>
        <p className="text-gray-500 text-sm">No {isPitcherGallery ? 'pitchers' : 'players'} added yet</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {sortedPlayers.map((player, index) => (
        <PlayerCard
          key={player.id}
          player={player}
          stats={statsMap[player.id]}
          rank={index + 1}
          isPitcher={isPitcherGallery}
          onEdit={onEditPlayer}
          onView={onViewPlayer || onEditPlayer}
          onDelete={onDeletePlayer}
          loading={loading && !statsMap[player.id]}
        />
      ))}
    </div>
  );
};
